import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";

// Casca ÚNICA do painel de config de um nó (padrão para TODOS os tipos).
// Header de identidade + abas [Configuração | Teste & resultados] + rodapé fixo.
// O conteúdo (config/test/footer) é injetado por props; a casca é sempre igual.
const useStyles = makeStyles(theme => {
  const a = theme.app;
  return {
    root: {
      width: 360,
      flexShrink: 0,
      borderLeft: `1px solid ${a.border}`,
      background: "#fff",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      // O MUI aplica typography.body1 (16px) em TODO input, enquanto o painel
      // roda em 12–12.5px (hint/leadText) — os campos ficavam visivelmente
      // maiores que o texto ao redor. Alinha a escala aqui, na casca, para valer
      // em qualquer tipo de nó (nem todo campo usa a classe `field`).
      "& .MuiInputBase-input": { fontSize: 13, letterSpacing: 0 },
      "& .MuiInputLabel-root": { fontSize: 13, letterSpacing: 0 },
      "& .MuiFormHelperText-root": { fontSize: 11.5 },
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        borderLeft: "none",
        borderTop: `1px solid ${a.border}`,
        height: "auto",
      },
    },
    header: { padding: "14px 14px 0", flexShrink: 0 },
    idRow: { display: "flex", alignItems: "center", gap: 10 },
    idMain: { flex: 1, minWidth: 0 },
    nameInput: {
      width: "100%",
      boxSizing: "border-box",
      border: "1px solid transparent",
      borderRadius: 7,
      background: "transparent",
      font: "inherit",
      fontSize: 16,
      fontWeight: 600,
      color: a.ink,
      padding: "3px 7px",
      margin: "-3px -7px",
      "&:hover": { background: a.hover },
      "&:focus": { outline: "none", background: "#fff", borderColor: a.borderInput },
    },
    nameStatic: { fontSize: 16, fontWeight: 600, color: a.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    tag: { fontFamily: a.mono, fontSize: 11, color: a.textTertiary, flexShrink: 0, letterSpacing: "0.02em" },
    close: { color: a.textTertiary, padding: 4, flexShrink: 0, "&:hover": { color: a.ink } },
    subtitle: { fontSize: 12.5, color: a.textSecondary, marginTop: 8, lineHeight: 1.45 },

    tabsWrap: { padding: "12px 14px 0", flexShrink: 0 },
    tabs: { display: "flex", gap: 3, background: "#F0F2F4", borderRadius: 9, padding: 3 },
    tab: {
      flex: 1, textAlign: "center", border: "none", background: "transparent", font: "inherit",
      fontSize: 12.5, fontWeight: 600, color: a.textSecondary, padding: "6px 10px", borderRadius: 7, cursor: "pointer",
    },
    tabOn: { background: "#fff", color: a.ink, boxShadow: "0 1px 3px rgba(27,34,42,.10)" },

    body: { flex: 1, overflowY: "auto", padding: 14, minHeight: 0 },
    footer: { borderTop: `1px solid ${a.border}`, padding: "10px 14px", flexShrink: 0 },
  };
});

const NodeConfigShell = ({
  icon,
  name,
  onName,
  nameEditable,
  tag,
  subtitle,
  onClose,
  footer,
  config,
  test,
  testLabel = "Teste & resultados",
  // aba controlada pelo pai (ConfigDrawer) para a seleção SOBREVIVER à troca de
  // nó: cada tipo de nó renderiza uma casca diferente, então um estado interno
  // seria perdido no remount. Sem esses props o componente segue funcionando
  // sozinho, com estado próprio.
  tab: tabProp,
  onTabChange,
}) => {
  const classes = useStyles();
  const [innerTab, setInnerTab] = useState("config");
  const tab = tabProp === undefined ? innerTab : tabProp;
  const setTab = v => {
    setInnerTab(v);
    if (onTabChange) onTabChange(v);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.idRow}>
          {icon}
          <div className={classes.idMain}>
            {nameEditable ? (
              <input
                className={classes.nameInput}
                value={name || ""}
                onChange={e => onName && onName(e.target.value)}
                placeholder="Nome"
              />
            ) : (
              <div className={classes.nameStatic}>{name}</div>
            )}
          </div>
          {tag && <span className={classes.tag}>{tag}</span>}
          {onClose && (
            <IconButton className={classes.close} size="small" onClick={onClose} title="Fechar">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
        {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
      </div>

      <div className={classes.tabsWrap}>
        <div className={classes.tabs}>
          <button type="button" className={clsx(classes.tab, tab === "config" && classes.tabOn)} onClick={() => setTab("config")}>
            Configuração
          </button>
          <button type="button" className={clsx(classes.tab, tab === "test" && classes.tabOn)} onClick={() => setTab("test")}>
            {testLabel}
          </button>
        </div>
      </div>

      <div className={classes.body}>{tab === "config" ? config : test}</div>

      {footer && <div className={classes.footer}>{footer}</div>}
    </div>
  );
};

export default NodeConfigShell;
